/**
 * Analytics Settings Tab — toggle which datasets are visible to suppliers
 * on the /reports (Analytics) page.
 *
 * Datasets are the data domains backed by the FieldRegistry DSIDs
 * (Customers, Contracts, Questionnaires, Approvals, Credit Score). When
 * disabled here, the corresponding card stops appearing on the supplier
 * Analytics page. Saved queries against a disabled dataset still exist
 * server-side; they're just hidden from the picker until re-enabled.
 */
import { useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../ui/card';
import { Switch } from '../../ui/switch';
import { Badge } from '../../ui/badge';
import { Database, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

import { reportingAPI, type Dataset } from '../../../lib/api';

export function AnalyticsTab() {
  const [datasets, setDatasets] = useState<Dataset[]>([]);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState<number | null>(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await reportingAPI.getDatasets({ page_size: 200 });
        if (!alive) return;
        setDatasets(r.data.results || []);
      } catch (err: unknown) {
        const msg = err instanceof Error ? err.message : 'Failed to load datasets';
        toast.error(msg);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  const toggle = async (ds: Dataset, next: boolean) => {
    setSavingId(ds.id);
    try {
      await reportingAPI.updateDataset(ds.id, { is_enabled: next });
      setDatasets((prev) => prev.map((d) => (d.id === ds.id ? { ...d, is_enabled: next } : d)));
      toast.success(`${ds.name} ${next ? 'enabled' : 'disabled'}`);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to update dataset';
      toast.error(msg);
    } finally {
      setSavingId(null);
    }
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Database className="h-5 w-5" />
            Analytics Datasets
          </CardTitle>
          <CardDescription>
            Choose which data domains your customers can see on the Analytics
            page. Disabling a dataset hides it from the picker — saved queries
            on it remain stored and reappear when you re-enable.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12 text-muted-foreground">
              <Loader2 className="h-5 w-5 animate-spin mr-2" />
              Loading datasets…
            </div>
          ) : datasets.length === 0 ? (
            <p className="text-sm text-muted-foreground py-6 text-center">
              No datasets are configured for your company.
            </p>
          ) : (
            <div className="divide-y rounded-md border">
              {datasets.map((ds) => (
                <div key={ds.id} className="flex items-center justify-between p-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-sm">{ds.name}</span>
                      {ds.dsid !== undefined && ds.dsid !== null && (
                        <Badge variant="outline" className="text-[10px]">
                          DSID {ds.dsid}
                        </Badge>
                      )}
                      <Badge
                        variant={ds.is_enabled ? 'default' : 'secondary'}
                        className="text-[10px]"
                      >
                        {ds.is_enabled ? 'Visible to customers' : 'Hidden'}
                      </Badge>
                    </div>
                    {ds.description && (
                      <p className="text-xs text-muted-foreground mt-1">{ds.description}</p>
                    )}
                    <div className="flex items-center gap-3 text-[11px] text-muted-foreground mt-1">
                      <span>{ds.available_field_count ?? 0} field{(ds.available_field_count ?? 0) === 1 ? '' : 's'}</span>
                      <span>·</span>
                      <span>{ds.saved_query_count ?? 0} saved quer{(ds.saved_query_count ?? 0) === 1 ? 'y' : 'ies'}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 ml-4">
                    {savingId === ds.id ? (
                      <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                    ) : null}
                    <Switch
                      checked={ds.is_enabled}
                      onCheckedChange={(checked) => toggle(ds, checked)}
                      disabled={savingId === ds.id}
                    />
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
